import { useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { Button } from '@mui/material';

import DrawerBody from 'src/components/drawers/DrawerBody';
import DrawerFooter from 'src/components/drawers/DrawerFooter';
import DrawerTitle from 'src/components/drawers/DrawerTitle';
import { OtpCodeInput } from 'src/components/fragments/OtpCodeInput';
import { useVerifyPhoneNumberMutation } from 'src/store/sageAdminApi';

interface InputOneTimePasscodeProps {
    phoneNumber: string;
    staffMemberId: string;
    onSendCode: () => Promise<void>;
    onBack: () => void;
    onSuccess: () => void;
}

export function InputOneTimePasscode(props: InputOneTimePasscodeProps) {
    const { phoneNumber, staffMemberId, onSendCode, onBack, onSuccess } = props;

    const [otp, setOtp] = useState('');
    const [hasError, setHasError] = useState(false);

    const [verifyPhoneNumber, { isLoading: isVerifying }] = useVerifyPhoneNumberMutation();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await verifyPhoneNumber({
                staffMemberId,
                body: { phoneNumber, code: otp },
            }).unwrap();
            onSuccess();
        } catch (error: any) {
            setHasError(true);
            setOtp('');
            datadogRum.addError(error, { message: 'Error verifying phone number', staffMemberId });
        }
    };

    const handleResend = async () => {
        setHasError(false);
        try {
            await onSendCode();
        } catch (error: any) {
            datadogRum.addError(error, {
                message: 'Error resending verification code',
                staffMemberId,
            });
        }
    };

    return (
        <form id="otp-form" onSubmit={handleSubmit}>
            <DrawerTitle title="Enter verification code" />
            <DrawerBody sx={{ p: 2.5 }}>
                <OtpCodeInput
                    otp={otp}
                    phoneNumber={phoneNumber}
                    hasError={hasError}
                    onResend={handleResend}
                    onOtpChange={setOtp}
                />
            </DrawerBody>
            <DrawerFooter sx={{ borderTop: 1, borderColor: 'grey.200', gap: 1.5, width: '100%' }}>
                <Button
                    fullWidth
                    disableElevation
                    color="primary"
                    variant="contained"
                    type="submit"
                    loading={isVerifying}
                    disabled={otp.length < 6 || isVerifying}
                >
                    Verify
                </Button>
                <Button fullWidth variant="text" onClick={onBack} disabled={isVerifying}>
                    Go Back
                </Button>
            </DrawerFooter>
        </form>
    );
}
